/**
 * Page de Gestion des Réclamations de Notes
 * Permet d'ouvrir, consulter, traiter des réclamations pour devoirs ET examens.
 * Fournit les preuves : note enregistrée, historique d'audit, scan OCR, fichier joint.
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  AlertTriangle, CheckCircle2, Clock, XCircle, Plus, Search,
  FileText, Upload, Printer, ChevronDown, ChevronUp, Eye,
  History, ShieldCheck, User, BookOpen, X, Filter, ScanLine, ExternalLink,
} from 'lucide-react';
import {
  useGetReclamationsQuery,
  useGetReclamationQuery,
  useCreateReclamationMutation,
  useCreateReclamationAvecFichierMutation,
  useTraiterReclamationMutation,
  useGetReclamationStatsQuery,
  type Reclamation,
  type StatutReclamation,
  type TypeNote,
  type OcrDevoirDetail,
  type OcrExamenDetail,
} from '../../services/reclamationsApi';
import { useGetListeInscritsQuery } from '../../services/etudiantsApi';
import { useGetMatieresQuery } from '../../services/matieresApi';
import { useGetNotesDevoirsQuery } from '../../services/notesDevoirsApi';
import { useGetNotesExamensQuery } from '../../services/notesExamensApi';
import { useGetAnneeActiveQuery } from '../../services/anneeAcademiqueApi';
import ScrollToTop from '../../components/ui/ScrollToTop';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUT_CONFIG: Record<StatutReclamation, { label: string; color: string; icon: React.ReactNode }> = {
  ouverte:   { label: 'Ouverte',   color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',      icon: <AlertTriangle size={13} /> },
  en_examen: { label: 'En examen', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300', icon: <Clock size={13} /> },
  acceptee:  { label: 'Acceptée',  color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',   icon: <CheckCircle2 size={13} /> },
  rejetee:   { label: 'Rejetée',   color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',          icon: <XCircle size={13} /> },
};

const StatutBadge: React.FC<{ statut: StatutReclamation }> = ({ statut }) => {
  const cfg = STATUT_CONFIG[statut];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      {cfg.icon}{cfg.label}
    </span>
  );
};

const fmt = (d: string | null | undefined): string => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return d; }
};

const fmtNote = (n: string | number | null | undefined): string => {
  if (n === null || n === undefined || n === '') return '—';
  const v = parseFloat(String(n));
  return isNaN(v) ? '—' : v.toFixed(2);
};

// ─── Impression attestation ───────────────────────────────────────────────────

const imprimerAttestation = (r: Reclamation): void => {
  const today = new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
  const html = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>Attestation de réclamation - ${r.etudiant_matricule}</title>
  <style>
    body{font-family:'Times New Roman',serif;max-width:700px;margin:60px auto;color:#111;font-size:14px}
    h1{text-align:center;font-size:20px;text-transform:uppercase;letter-spacing:2px;margin-bottom:8px}
    .sub{text-align:center;font-size:13px;color:#555;margin-bottom:36px}
    table{width:100%;border-collapse:collapse;margin:20px 0}
    td{padding:7px 12px;border:1px solid #ccc}
    td:first-child{font-weight:bold;width:40%;background:#f5f5f5}
    .section{margin-top:24px}
    .section h2{font-size:14px;border-bottom:1px solid #111;padding-bottom:4px;margin-bottom:10px;text-transform:uppercase}
    .decision{margin-top:24px;padding:14px;border:2px solid #333;border-radius:4px}
    .accepted{border-color:green;background:#f0fff0}
    .rejected{border-color:#c00;background:#fff0f0}
    .footer{margin-top:60px;display:flex;justify-content:space-between}
    .sig{text-align:center;width:220px}
    .sig p{border-top:1px solid #999;margin-top:60px;padding-top:8px;font-size:12px;color:#555}
    @media print{body{margin:0}}
  </style>
</head>
<body>
  <h1>Attestation de Réclamation de Note</h1>
  <p class="sub">Émise le ${today}</p>
  <div class="section">
    <h2>Étudiant</h2>
    <table>
      <tr><td>Matricule</td><td>${r.etudiant_matricule}</td></tr>
      <tr><td>Nom &amp; Prénoms</td><td>${r.etudiant_nom} ${r.etudiant_prenoms}</td></tr>
    </table>
  </div>
  <div class="section">
    <h2>Note concernée</h2>
    <table>
      <tr><td>Matière</td><td>${r.matiere_code} – ${r.matiere_nom}</td></tr>
      <tr><td>Type</td><td>${r.type_note_display}${r.champ_conteste_display ? ` — ${r.champ_conteste_display}` : ''}</td></tr>
      <tr><td>Année académique</td><td>${r.annee_academique}</td></tr>
      <tr><td>Semestre</td><td>${r.semestre}</td></tr>
      <tr><td>Note originale</td><td>${fmtNote(r.note_originale)} / 20</td></tr>
      <tr><td>Note réclamée</td><td>${fmtNote(r.note_attendue)} / 20</td></tr>
    </table>
  </div>
  <div class="section">
    <h2>Motif</h2>
    <p>${r.motif || '—'}</p>
  </div>
  <div class="section">
    <h2>Décision</h2>
    <div class="decision ${r.statut === 'acceptee' ? 'accepted' : r.statut === 'rejetee' ? 'rejected' : ''}">
      <p><strong>Statut :</strong> ${STATUT_CONFIG[r.statut]?.label ?? r.statut}</p>
      ${r.note_corrigee ? `<p><strong>Note accordée :</strong> ${fmtNote(r.note_corrigee)} / 20</p>` : ''}
      ${r.decision_commentaire ? `<p><strong>Commentaire :</strong> ${r.decision_commentaire}</p>` : ''}
      ${r.traitee_par_nom ? `<p><strong>Traité par :</strong> ${r.traitee_par_nom}</p>` : ''}
      ${r.date_traitement ? `<p><strong>Date :</strong> ${fmt(r.date_traitement)}</p>` : ''}
    </div>
  </div>
  <div class="footer">
    <div class="sig"><p>Signature de l'étudiant</p></div>
    <div class="sig"><p>Cachet &amp; Signature administration</p></div>
  </div>
</body>
</html>`;
  const w = window.open('', '_blank', 'width=800,height=900');
  if (!w) return;
  w.document.write(html);
  w.document.close();
  setTimeout(() => w.print(), 600);
};

// ─── Modal Détail ─────────────────────────────────────────────────────────────

interface ModalDetailProps { id: number; onClose: () => void; }

const ModalDetail: React.FC<ModalDetailProps> = ({ id, onClose }) => {
  const { data: r, isLoading, refetch } = useGetReclamationQuery(id);
  const [traiter, { isLoading: isSaving }] = useTraiterReclamationMutation();

  const [decision, setDecision] = useState<'en_examen' | 'acceptee' | 'rejetee' | ''>('');
  const [noteCorrigee, setNoteCorrigee] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [msgDecision, setMsgDecision] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [showHistorique, setShowHistorique] = useState(false);

  useEffect(() => {
    if (r && r.statut !== 'ouverte') {
      setDecision(r.statut as 'en_examen' | 'acceptee' | 'rejetee');
      setCommentaire(r.decision_commentaire || '');
      if (r.note_corrigee) setNoteCorrigee(r.note_corrigee);
    }
  }, [r]);

  const handleTraiter = async () => {
    if (!decision || !r) return;
    setMsgDecision(null);
    try {
      await traiter({
        id,
        decision,
        note_corrigee: decision === 'acceptee' && noteCorrigee ? parseFloat(noteCorrigee) : null,
        decision_commentaire: commentaire,
      }).unwrap();
      setMsgDecision({ type: 'ok', text: 'Décision enregistrée avec succès.' });
      refetch();
    } catch (e: any) {
      setMsgDecision({ type: 'err', text: e?.data?.error || "Erreur lors de l'enregistrement." });
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-xl text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-gray-600 dark:text-gray-400">Chargement…</p>
        </div>
      </div>
    );
  }
  if (!r) return null;

  const hasHistorique = r.historique && r.historique.length > 0;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center pt-8 pb-8 px-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <AlertTriangle size={20} className="text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Réclamation #{r.id}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Ouverte le {fmt(r.date_ouverture)}{r.ouverte_par_nom ? ` par ${r.ouverte_par_nom}` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <StatutBadge statut={r.statut} />
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
              <X size={20} className="text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Étudiant + Note */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <User size={15} className="text-blue-500" />
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Étudiant</span>
              </div>
              <p className="text-base font-bold text-gray-900 dark:text-gray-100">
                {r.etudiant_nom} {r.etudiant_prenoms}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{r.etudiant_matricule}</p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <BookOpen size={15} className="text-purple-500" />
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Note concernée</span>
              </div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {r.matiere_code} — {r.matiere_nom}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {r.type_note_display}
                {r.champ_conteste_display && (
                  <span className="ml-1 px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-[10px] font-medium">
                    {r.champ_conteste_display}
                  </span>
                )}
                {' · '}{r.semestre} · {r.annee_academique}
              </p>
              <div className="mt-2 flex items-center gap-3">
                <div className="text-center">
                  <p className="text-xs text-gray-400 dark:text-gray-500">Originale</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{fmtNote(r.note_originale)}</p>
                </div>
                <span className="text-gray-300 dark:text-gray-600">→</span>
                <div className="text-center">
                  <p className="text-xs text-gray-400 dark:text-gray-500">Réclamée</p>
                  <p className="text-lg font-bold text-orange-600 dark:text-orange-400">{fmtNote(r.note_attendue)}</p>
                </div>
                {r.note_corrigee && (
                  <>
                    <span className="text-gray-300 dark:text-gray-600">→</span>
                    <div className="text-center">
                      <p className="text-xs text-gray-400 dark:text-gray-500">Accordée</p>
                      <p className="text-lg font-bold text-green-600 dark:text-green-400">{fmtNote(r.note_corrigee)}</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Motif */}
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Motif</p>
            <p className="text-sm text-gray-700 dark:text-gray-300 bg-orange-50 dark:bg-orange-900/10 border border-orange-100 dark:border-orange-900/30 rounded-lg px-3 py-2">
              {r.motif || '—'}
            </p>
          </div>

          {/* Preuves */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 flex items-center gap-2">
              <ShieldCheck size={15} className="text-blue-500" />
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Preuves &amp; Traçabilité</span>
            </div>
            <div className="p-4 space-y-4">
              {/* Détail note liée */}
              {r.note_devoir_detail ? (
                <div className="rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
                  <div className="px-3 py-2 bg-purple-50 dark:bg-purple-900/20 text-xs font-semibold text-purple-700 dark:text-purple-300 flex items-center gap-1.5">
                    <BookOpen size={12} />
                    Note de devoir enregistrée dans le système
                  </div>
                  <div className="grid grid-cols-3 divide-x divide-gray-200 dark:divide-gray-600">
                    <div className="p-3 text-center">
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase font-medium mb-0.5">Devoir 1</p>
                      {r.note_devoir_detail.absent1
                        ? <p className="text-sm font-bold text-orange-500">Abs</p>
                        : <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{fmtNote(r.note_devoir_detail.note1)}</p>}
                    </div>
                    <div className="p-3 text-center">
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase font-medium mb-0.5">Devoir 2</p>
                      {r.note_devoir_detail.absent2
                        ? <p className="text-sm font-bold text-orange-500">Abs</p>
                        : <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{fmtNote(r.note_devoir_detail.note2)}</p>}
                    </div>
                    <div className="p-3 text-center bg-blue-50 dark:bg-blue-900/10">
                      <p className="text-[10px] text-blue-600 dark:text-blue-400 uppercase font-medium mb-0.5">Moyenne</p>
                      <p className="text-lg font-bold text-blue-700 dark:text-blue-300">{fmtNote(r.note_devoir_detail.moyenne_devoirs)}</p>
                    </div>
                  </div>
                  <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700/50 text-[10px] text-gray-500 dark:text-gray-400 flex flex-wrap gap-3">
                    {r.note_devoir_detail.verrouille && <span className="text-amber-600 dark:text-amber-400 font-medium">🔒 Verrouillé</span>}
                    {r.note_devoir_detail.saisi_par && <span>Saisi par : <strong>{r.note_devoir_detail.saisi_par}</strong></span>}
                    {r.note_devoir_detail.date_saisie && <span>le {fmt(r.note_devoir_detail.date_saisie)}</span>}
                    {r.note_devoir_detail.modifie_par && <span>· Modifié par : <strong>{r.note_devoir_detail.modifie_par}</strong></span>}
                  </div>
                </div>
              ) : r.note_examen_detail ? (
                <div className="rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
                  <div className="px-3 py-2 bg-purple-50 dark:bg-purple-900/20 text-xs font-semibold text-purple-700 dark:text-purple-300 flex items-center gap-1.5">
                    <BookOpen size={12} />
                    Note d'examen enregistrée dans le système
                    {r.note_examen_detail.est_rattrapage && (
                      <span className="ml-auto px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full text-[10px]">Rattrapage</span>
                    )}
                  </div>
                  <div className="p-3 flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase font-medium mb-0.5">Note examen</p>
                      {r.note_examen_detail.absent
                        ? <p className="text-lg font-bold text-orange-500">Absent</p>
                        : <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{fmtNote(r.note_examen_detail.note_examen)}</p>}
                    </div>
                  </div>
                  <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700/50 text-[10px] text-gray-500 dark:text-gray-400 flex flex-wrap gap-3">
                    {r.note_examen_detail.verrouille && <span className="text-amber-600 dark:text-amber-400 font-medium">🔒 Verrouillé</span>}
                    {r.note_examen_detail.saisi_par && <span>Saisi par : <strong>{r.note_examen_detail.saisi_par}</strong></span>}
                    {r.note_examen_detail.date_saisie && <span>le {fmt(r.note_examen_detail.date_saisie)}</span>}
                    {r.note_examen_detail.modifie_par && <span>· Modifié par : <strong>{r.note_examen_detail.modifie_par}</strong></span>}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                  Aucune note liée automatiquement dans le système
                </p>
              )}

              {/* Scan OCR — ligne de l'étudiant sur le relevé */}
              {(r.ocr_devoir_detail || r.ocr_examen_detail) && (() => {
                const ocr: OcrDevoirDetail | OcrExamenDetail = (r.ocr_devoir_detail || r.ocr_examen_detail)!;
                const isDevoir = !!r.ocr_devoir_detail;
                const ocrD = r.ocr_devoir_detail;
                const ocrE = r.ocr_examen_detail;
                return (
                  <div className="rounded-lg border border-teal-200 dark:border-teal-800 overflow-hidden">
                    <div className="px-3 py-2 bg-teal-50 dark:bg-teal-900/20 text-xs font-semibold text-teal-700 dark:text-teal-300 flex items-center gap-1.5">
                      <ScanLine size={12} />
                      Relevé scanné — ligne de l'étudiant
                      <span className="ml-auto text-[10px] font-normal text-teal-500 dark:text-teal-400">
                        {ocr.releve_code} · Page {ocr.numero_page}, ligne {ocr.numero_ligne}
                      </span>
                    </div>

                    {/* Image de la ligne */}
                    {ocr.image_base64 ? (
                      <div className="p-3 bg-white dark:bg-gray-900">
                        <p className="text-[10px] text-gray-400 dark:text-gray-500 mb-1.5 uppercase tracking-wide">
                          Extrait du relevé — ligne de l'étudiant
                        </p>
                        <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                          <img
                            src={`data:image/png;base64,${ocr.image_base64}`}
                            alt={`Ligne ${ocr.numero_ligne} du relevé ${ocr.releve_code}`}
                            className="w-full object-contain max-h-32"
                            style={{ imageRendering: 'crisp-edges' }}
                          />
                        </div>
                      </div>
                    ) : ocr.image_ligne_url ? (
                      <div className="p-3">
                        <img
                          src={ocr.image_ligne_url}
                          alt={`Ligne ${ocr.numero_ligne}`}
                          className="w-full object-contain max-h-32 border border-gray-200 dark:border-gray-700 rounded-md"
                        />
                      </div>
                    ) : (
                      <div className="p-3 text-xs text-gray-400 dark:text-gray-500 italic">
                        Image non disponible — relevé code : {ocr.releve_code}
                      </div>
                    )}

                    {/* Notes du scan vs notes attendues */}
                    {isDevoir && ocrD && (
                      <div className="grid grid-cols-3 divide-x divide-teal-100 dark:divide-teal-900 border-t border-teal-100 dark:border-teal-900">
                        <div className="p-2 text-center">
                          <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase font-medium mb-0.5">D1 (scan)</p>
                          {ocrD.absent_d1
                            ? <p className="text-sm font-bold text-orange-500">Abs</p>
                            : <p className="text-base font-bold text-teal-700 dark:text-teal-300">{fmtNote(ocrD.note_d1_validee)}</p>}
                        </div>
                        <div className="p-2 text-center">
                          <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase font-medium mb-0.5">D2 (scan)</p>
                          {ocrD.absent_d2
                            ? <p className="text-sm font-bold text-orange-500">Abs</p>
                            : <p className="text-base font-bold text-teal-700 dark:text-teal-300">{fmtNote(ocrD.note_d2_validee)}</p>}
                        </div>
                        <div className="p-2 text-center">
                          <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase font-medium mb-0.5">Relevé</p>
                          <p className="text-[10px] text-teal-600 dark:text-teal-400 font-medium truncate">{ocrD.releve_matiere || '—'}</p>
                        </div>
                      </div>
                    )}

                    {!isDevoir && ocrE && (
                      <div className="grid grid-cols-2 divide-x divide-teal-100 dark:divide-teal-900 border-t border-teal-100 dark:border-teal-900">
                        <div className="p-2 text-center">
                          <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase font-medium mb-0.5">Note (scan)</p>
                          {ocrE.absent_valide
                            ? <p className="text-sm font-bold text-orange-500">Absent</p>
                            : <p className="text-base font-bold text-teal-700 dark:text-teal-300">{fmtNote(ocrE.note_finale)}</p>}
                        </div>
                        <div className="p-2 text-center">
                          <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase font-medium mb-0.5">Source</p>
                          <p className="text-[10px] text-teal-600 dark:text-teal-400 font-medium">{ocrE.source_note}</p>
                        </div>
                      </div>
                    )}

                    {/* Liens vers le document complet */}
                    <div className="px-3 py-2 bg-teal-50/50 dark:bg-teal-900/10 border-t border-teal-100 dark:border-teal-900 flex flex-wrap items-center gap-3 text-[10px]">
                      {ocr.releve_date_scan && (
                        <span className="text-teal-600 dark:text-teal-400">Scanné le : {fmt(ocr.releve_date_scan)}</span>
                      )}
                      {ocr.releve_scan_url && (
                        <a
                          href={ocr.releve_scan_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-auto inline-flex items-center gap-1 text-teal-600 dark:text-teal-400 hover:underline font-medium"
                        >
                          <ExternalLink size={10} />
                          Voir le relevé complet
                        </a>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Fichier preuve */}
              {r.fichier_preuve_url && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Fichier preuve joint</p>
                  <a
                    href={r.fichier_preuve_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    <FileText size={14} />Voir le document
                  </a>
                  {r.commentaire_preuve && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{r.commentaire_preuve}</p>
                  )}
                </div>
              )}

              {/* Historique d'audit */}
              <div>
                <button
                  onClick={() => setShowHistorique(!showHistorique)}
                  className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                >
                  <History size={14} />
                  Historique d'audit
                  {hasHistorique && (
                    <span className="px-1.5 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                      {r.historique.length}
                    </span>
                  )}
                  {showHistorique ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                </button>

                {showHistorique && (
                  <div className="mt-2 overflow-x-auto">
                    {hasHistorique ? (
                      <table className="min-w-full text-xs border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            {['Date', 'Par', 'Champ', 'Avant', 'Après', 'Motif'].map(h => (
                              <th key={h} className="px-3 py-2 text-left text-gray-500 dark:text-gray-400 font-medium whitespace-nowrap">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                          {r.historique.map((h, i) => (
                            <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                              <td className="px-3 py-1.5 whitespace-nowrap text-gray-500 dark:text-gray-400">{fmt(h.date)}</td>
                              <td className="px-3 py-1.5 whitespace-nowrap text-gray-700 dark:text-gray-300">{h.modifie_par}</td>
                              <td className="px-3 py-1.5 whitespace-nowrap text-gray-500 dark:text-gray-400">{h.champ}</td>
                              <td className="px-3 py-1.5 text-red-600 dark:text-red-400">{h.ancienne_valeur}</td>
                              <td className="px-3 py-1.5 text-green-600 dark:text-green-400">{h.nouvelle_valeur}</td>
                              <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">{h.motif}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-xs text-gray-400 dark:text-gray-500 italic mt-1">
                        Aucune modification enregistrée pour cette note
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Décision */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Décision</span>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Action</label>
                <select
                  value={decision}
                  onChange={(e) => setDecision(e.target.value as 'en_examen' | 'acceptee' | 'rejetee' | '')}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">— Choisir une action —</option>
                  <option value="en_examen">Passer en examen</option>
                  <option value="acceptee">Accepter la réclamation</option>
                  <option value="rejetee">Rejeter la réclamation</option>
                </select>
              </div>

              {decision === 'acceptee' && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Note accordée (optionnel)
                  </label>
                  <input
                    type="number" min={0} max={20} step={0.25}
                    value={noteCorrigee}
                    onChange={(e) => setNoteCorrigee(e.target.value)}
                    placeholder="ex: 15.00"
                    className="w-40 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Commentaire</label>
                <textarea
                  rows={2}
                  value={commentaire}
                  onChange={(e) => setCommentaire(e.target.value)}
                  placeholder="Expliquer la décision…"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              {msgDecision && (
                <div className={`p-3 rounded-lg text-sm ${msgDecision.type === 'ok' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'}`}>
                  {msgDecision.text}
                </div>
              )}

              <div className="flex items-center gap-3 pt-1">
                <button
                  onClick={handleTraiter}
                  disabled={!decision || isSaving}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Enregistrement…' : 'Valider la décision'}
                </button>
                <button
                  onClick={() => imprimerAttestation(r)}
                  className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <Printer size={15} />
                  Imprimer attestation
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Formulaire Nouvelle Réclamation ──────────────────────────────────────────

interface FormProps { anneeAcademique: string; onCreated: () => void; }

const FormNouvelleReclamation: React.FC<FormProps> = ({ anneeAcademique, onCreated }) => {
  const { data: etudiants } = useGetListeInscritsQuery();
  const { data: matieres } = useGetMatieresQuery();

  const [etudiantId, setEtudiantId] = useState<number | ''>('');
  const [typeNote, setTypeNote] = useState<TypeNote>('devoir');
  const [champConteste, setChampConteste] = useState<string>('D1');
  const [matiereId, setMatiereId] = useState<number | ''>('');
  const [semestre, setSemestre] = useState<'S1' | 'S2'>('S1');
  const [noteAttendue, setNoteAttendue] = useState('');
  const [motif, setMotif] = useState('');
  const [fichierPreuve, setFichierPreuve] = useState<File | null>(null);
  const [commentairePreuve, setCommentairePreuve] = useState('');
  const [searchEtudiant, setSearchEtudiant] = useState('');
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [createReclamation, { isLoading: isCreating }] = useCreateReclamationMutation();
  const [createAvecFichier, { isLoading: isCreatingFile }] = useCreateReclamationAvecFichierMutation();
  const isSaving = isCreating || isCreatingFile;

  const shouldFetchDevoir = Boolean(etudiantId && matiereId && typeNote === 'devoir');
  const shouldFetchExamen = Boolean(etudiantId && matiereId && typeNote === 'examen');

  const { data: notesDevoirs } = useGetNotesDevoirsQuery(
    { etudiant_id: etudiantId as number, matiere_id: matiereId as number, annee_academique: anneeAcademique, semestre },
    { skip: !shouldFetchDevoir }
  );
  const { data: notesExamens } = useGetNotesExamensQuery(
    { etudiant_id: etudiantId as number, matiere_id: matiereId as number, annee_academique: anneeAcademique, semestre },
    { skip: !shouldFetchExamen }
  );

  const noteActuelle = useMemo(() => {
    if (typeNote === 'devoir' && notesDevoirs && notesDevoirs.length > 0) {
      const n = notesDevoirs[0];
      const valeurChamp =
        champConteste === 'D1'        ? n.note1 :
        champConteste === 'D2'        ? n.note2 :
        champConteste === 'moy_devoir' ? n.moyenne_devoirs :
        n.moyenne_devoirs;
      const labelChamp =
        champConteste === 'D1'        ? `D1 : ${fmtNote(n.note1)}` :
        champConteste === 'D2'        ? `D2 : ${fmtNote(n.note2)}` :
        champConteste === 'moy_devoir' ? `Moy. devoirs : ${fmtNote(n.moyenne_devoirs)}` :
        `Moy. devoirs : ${fmtNote(n.moyenne_devoirs)}`;
      return {
        id: n.id,
        valeur: valeurChamp,
        label: `${labelChamp} — (D1 : ${fmtNote(n.note1)}, D2 : ${fmtNote(n.note2)}, Moy : ${fmtNote(n.moyenne_devoirs)})`,
      };
    }
    if (typeNote === 'examen' && notesExamens && notesExamens.length > 0) {
      const n = notesExamens.find(x => !x.est_rattrapage) ?? notesExamens[0];
      return { id: n.id, valeur: n.note_examen, label: `Note examen : ${fmtNote(n.note_examen)}` };
    }
    return null;
  }, [notesDevoirs, notesExamens, typeNote, champConteste]);

  // Étudiant sélectionné (pour filtrer les matières par option)
  const etudiantSelectionne = useMemo(() => {
    if (!etudiantId || !etudiants) return null;
    return etudiants.find(e => e.id === etudiantId) || null;
  }, [etudiants, etudiantId]);

  // Matières filtrées par l'option de l'étudiant sélectionné
  const matieresFiltrees = useMemo(() => {
    if (!matieres || !Array.isArray(matieres)) return [];
    if (!etudiantSelectionne?.option) return matieres;
    return matieres.filter(m => m.option === etudiantSelectionne.option);
  }, [matieres, etudiantSelectionne]);

  const etudiantsFiltres = useMemo(() => {
    if (!etudiants) return [];
    if (!searchEtudiant.trim()) return etudiants;
    const q = searchEtudiant.toLowerCase();
    return etudiants.filter(e =>
      e.matricule.toLowerCase().includes(q) ||
      e.nom.toLowerCase().includes(q) ||
      e.prenoms.toLowerCase().includes(q)
    );
  }, [etudiants, searchEtudiant]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    if (!etudiantId || !matiereId || !motif.trim()) {
      setMsg({ type: 'err', text: 'Veuillez remplir tous les champs obligatoires.' });
      return;
    }
    const base: Record<string, string | number | null | undefined> = {
      etudiant: etudiantId as number,
      type_note: typeNote,
      champ_conteste: champConteste,
      matiere: matiereId as number,
      annee_academique: anneeAcademique,
      semestre,
      note_originale: noteActuelle?.valeur ? parseFloat(String(noteActuelle.valeur)) : undefined,
      note_attendue: noteAttendue ? parseFloat(noteAttendue) : undefined,
      motif,
      commentaire_preuve: commentairePreuve || undefined,
      note_devoir: typeNote === 'devoir' && noteActuelle ? noteActuelle.id : undefined,
      note_examen: typeNote === 'examen' && noteActuelle ? noteActuelle.id : undefined,
    };
    try {
      if (fichierPreuve) {
        const fd = new FormData();
        Object.entries(base).forEach(([k, v]) => { if (v !== undefined && v !== null) fd.append(k, String(v)); });
        fd.append('fichier_preuve', fichierPreuve);
        await createAvecFichier(fd).unwrap();
      } else {
        await createReclamation(base as any).unwrap();
      }
      setMsg({ type: 'ok', text: 'Réclamation créée avec succès.' });
      setEtudiantId(''); setMatiereId(''); setMotif('');
      setNoteAttendue(''); setCommentairePreuve('');
      setFichierPreuve(null); setSearchEtudiant('');
      setChampConteste('D1');
      if (fileRef.current) fileRef.current.value = '';
      onCreated();
    } catch (err: any) {
      const d = err?.data;
      const errText = typeof d === 'string' ? d : d?.detail || d?.error || JSON.stringify(d) || 'Erreur inconnue.';
      setMsg({ type: 'err', text: errText });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 max-w-2xl">
      {/* Étudiant */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Étudiant <span className="text-red-500">*</span>
        </label>
        <div className="relative mb-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text" value={searchEtudiant}
            onChange={(e) => setSearchEtudiant(e.target.value)}
            placeholder="Rechercher par matricule ou nom…"
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={etudiantId}
          onChange={(e) => {
            const val = e.target.value ? parseInt(e.target.value) : '';
            setEtudiantId(val);
            setMatiereId(''); // Reset matière quand l'étudiant change
          }}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">— Sélectionner un étudiant —</option>
          {etudiantsFiltres.map(e => (
            <option key={e.id} value={e.id}>{e.matricule} — {e.nom} {e.prenoms}{e.classe ? ` (${e.classe})` : ''}</option>
          ))}
        </select>
      </div>

      {/* Type + Champ contesté + Matière + Semestre */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Type <span className="text-red-500">*</span>
          </label>
          <select
            value={typeNote}
            onChange={(e) => {
              setTypeNote(e.target.value as TypeNote);
              if (e.target.value === 'examen') setChampConteste('note_examen');
              else setChampConteste('D1');
            }}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="devoir">Note de Devoir</option>
            <option value="examen">Note d'Examen</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Champ contesté <span className="text-red-500">*</span>
          </label>
          {typeNote === 'devoir' ? (
            <select
              value={champConteste}
              onChange={(e) => setChampConteste(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="D1">Devoir 1 (D1)</option>
              <option value="D2">Devoir 2 (D2)</option>
              <option value="moy_devoir">Moyenne des devoirs</option>
            </select>
          ) : (
            <div className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
              Note d'examen
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Matière <span className="text-red-500">*</span>
          </label>
          <select
            value={matiereId}
            onChange={(e) => setMatiereId(e.target.value ? parseInt(e.target.value) : '')}
            disabled={!etudiantId}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">{etudiantId ? '— Sélectionner —' : '— Sélectionnez d\'abord un étudiant —'}</option>
            {matieresFiltrees.map(m => (
              <option key={m.id} value={m.id}>{m.code_matiere} — {m.nom_matiere}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Semestre</label>
          <select
            value={semestre}
            onChange={(e) => setSemestre(e.target.value as 'S1' | 'S2')}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="S1">Semestre 1</option>
            <option value="S2">Semestre 2</option>
          </select>
        </div>
      </div>

      {/* Note actuelle auto-détectée */}
      {noteActuelle && (
        <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <CheckCircle2 size={16} className="text-blue-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Note trouvée dans le système</p>
            <p className="text-xs text-blue-600 dark:text-blue-400">{noteActuelle.label}</p>
          </div>
        </div>
      )}

      {/* Note attendue */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Note attendue par l'étudiant (optionnel)
        </label>
        <input
          type="number" min={0} max={20} step={0.25}
          value={noteAttendue}
          onChange={(e) => setNoteAttendue(e.target.value)}
          placeholder="ex: 15.00"
          className="w-36 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Motif */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Motif <span className="text-red-500">*</span>
        </label>
        <textarea
          rows={3} value={motif}
          onChange={(e) => setMotif(e.target.value)}
          placeholder="Décrivez le motif de la réclamation…"
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
        />
      </div>

      {/* Fichier preuve */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Fichier preuve — scan, photo (optionnel)
        </label>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 px-3 py-2 text-sm border border-dashed border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600">
            <Upload size={14} />
            {fichierPreuve ? fichierPreuve.name : 'Choisir un fichier'}
            <input ref={fileRef} type="file" accept="image/*,.pdf"
              onChange={(e) => setFichierPreuve(e.target.files?.[0] ?? null)} className="hidden" />
          </label>
          {fichierPreuve && (
            <button type="button" onClick={() => { setFichierPreuve(null); if (fileRef.current) fileRef.current.value = ''; }}>
              <X size={14} className="text-gray-400 hover:text-red-500" />
            </button>
          )}
        </div>
        {fichierPreuve && (
          <input
            type="text" value={commentairePreuve}
            onChange={(e) => setCommentairePreuve(e.target.value)}
            placeholder="Note sur ce fichier (optionnel)"
            className="mt-2 w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        )}
      </div>

      {msg && (
        <div className={`p-3 rounded-lg text-sm border ${msg.type === 'ok'
          ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800'
          : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800'}`}>
          {msg.text}
        </div>
      )}

      <button
        type="submit"
        disabled={isSaving || !etudiantId || !matiereId || !motif.trim()}
        className="px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSaving ? 'Enregistrement…' : 'Ouvrir la réclamation'}
      </button>
    </form>
  );
};

// ─── Page principale ──────────────────────────────────────────────────────────

type Tab = 'liste' | 'nouvelle';

const ReclamationsNotes: React.FC = () => {
  const { data: anneeActive } = useGetAnneeActiveQuery();
  const anneeAcademique = anneeActive?.libelle ?? localStorage.getItem('annee_academique') ?? '';

  const [activeTab, setActiveTab] = useState<Tab>('liste');
  const [filtreStatut, setFiltreStatut] = useState<StatutReclamation | ''>('');
  const [filtreType, setFiltreType] = useState<TypeNote | ''>('');
  const [filtreAnnee, setFiltreAnnee] = useState('');
  const [filtreSemestre, setFiltreSemestre] = useState('');
  const [recherche, setRecherche] = useState('');
  const [modalId, setModalId] = useState<number | null>(null);

  useEffect(() => {
    if (anneeAcademique && !filtreAnnee) setFiltreAnnee(anneeAcademique);
  }, [anneeAcademique]);

  const apiFilters = {
    ...(filtreStatut ? { statut: filtreStatut as StatutReclamation } : {}),
    ...(filtreType ? { type_note: filtreType as TypeNote } : {}),
    ...(filtreAnnee ? { annee_academique: filtreAnnee } : {}),
    ...(filtreSemestre ? { semestre: filtreSemestre } : {}),
  };

  const { data: reclamations = [], isLoading, refetch } = useGetReclamationsQuery(apiFilters);
  const { data: stats } = useGetReclamationStatsQuery({ annee_academique: filtreAnnee || undefined });

  const reclamationsFiltrees = useMemo(() => {
    if (!recherche.trim()) return reclamations;
    const q = recherche.toLowerCase();
    return reclamations.filter(r =>
      r.etudiant_matricule.toLowerCase().includes(q) ||
      r.etudiant_nom.toLowerCase().includes(q) ||
      r.matiere_nom.toLowerCase().includes(q) ||
      r.motif.toLowerCase().includes(q)
    );
  }, [reclamations, recherche]);

  const STAT_CARDS = [
    { label: 'Ouvertes',  value: stats?.ouvertes ?? 0,  color: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-100 dark:border-blue-800' },
    { label: 'En examen', value: stats?.en_examen ?? 0, color: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 border-yellow-100 dark:border-yellow-800' },
    { label: 'Acceptées', value: stats?.acceptees ?? 0, color: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-100 dark:border-green-800' },
    { label: 'Rejetées',  value: stats?.rejetees ?? 0,  color: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-100 dark:border-red-800' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* En-tête */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Réclamations de Notes</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Gérez les réclamations avec preuves et historique d'audit — Devoirs &amp; Examens
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {STAT_CARDS.map(sc => (
          <div key={sc.label} className={`border rounded-xl p-4 ${sc.color}`}>
            <p className="text-2xl font-bold">{sc.value}</p>
            <p className="text-sm font-medium mt-0.5">{sc.label}</p>
          </div>
        ))}
      </div>

      {/* Onglets */}
      <div className="flex gap-2 mb-6">
        {([
          { key: 'liste',    label: 'Liste des réclamations', icon: <Filter size={15} /> },
          { key: 'nouvelle', label: 'Nouvelle réclamation',   icon: <Plus size={15} /> },
        ] as { key: Tab; label: string; icon: React.ReactNode }[]).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-blue-600 text-white shadow'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            {tab.icon}
            {tab.label}
            {tab.key === 'liste' && reclamationsFiltrees.length > 0 && (
              <span className={`px-1.5 py-0.5 text-xs rounded-full ${activeTab === 'liste' ? 'bg-white/20' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}>
                {reclamationsFiltrees.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Onglet Nouvelle */}
      {activeTab === 'nouvelle' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-5">
            Ouvrir une réclamation
          </h2>
          <FormNouvelleReclamation
            anneeAcademique={anneeAcademique}
            onCreated={() => { refetch(); setActiveTab('liste'); }}
          />
        </div>
      )}

      {/* Onglet Liste */}
      {activeTab === 'liste' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700">
          {/* Filtres */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-44">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text" value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                placeholder="Rechercher étudiant, matière…"
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {([
              { value: filtreStatut, setter: setFiltreStatut as (v: string) => void,
                options: [['', 'Tous statuts'], ['ouverte', 'Ouvertes'], ['en_examen', 'En examen'], ['acceptee', 'Acceptées'], ['rejetee', 'Rejetées']] },
              { value: filtreType,   setter: setFiltreType as (v: string) => void,
                options: [['', 'Tous types'], ['devoir', 'Devoirs'], ['examen', 'Examens']] },
              { value: filtreSemestre, setter: setFiltreSemestre,
                options: [['', 'Tous semestres'], ['S1', 'Semestre 1'], ['S2', 'Semestre 2']] },
            ] as { value: string; setter: (v: string) => void; options: string[][] }[]).map((f, i) => (
              <select key={i} value={f.value} onChange={(e) => f.setter(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500">
                {f.options.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
              </select>
            ))}
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : reclamationsFiltrees.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <AlertTriangle size={32} className="mx-auto mb-3 opacity-40" />
              <p>Aucune réclamation{recherche || filtreStatut || filtreType ? ' correspondant aux filtres' : ''}.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    {['#', 'Étudiant', 'Matière', 'Type', 'Note orig.', 'Note récl.', 'Statut', 'Ouverture', ''].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                  {reclamationsFiltrees.map(r => (
                    <tr
                      key={r.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                      onClick={() => setModalId(r.id)}
                    >
                      <td className="px-4 py-3 text-sm text-gray-400 dark:text-gray-500">#{r.id}</td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          {r.etudiant_nom} {r.etudiant_prenoms}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{r.etudiant_matricule}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{r.matiere_code}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 max-w-xs truncate">{r.matiere_nom}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                          r.type_note === 'devoir'
                            ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                            : 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                        }`}>
                          {r.type_note_display}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-gray-700 dark:text-gray-300">{fmtNote(r.note_originale)}</td>
                      <td className="px-4 py-3 text-sm font-mono text-orange-600 dark:text-orange-400">{fmtNote(r.note_attendue)}</td>
                      <td className="px-4 py-3"><StatutBadge statut={r.statut} /></td>
                      <td className="px-4 py-3 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">{fmt(r.date_ouverture)}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); setModalId(r.id); }}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                        >
                          <Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {modalId !== null && (
        <ModalDetail id={modalId} onClose={() => setModalId(null)} />
      )}

      {/* Bouton flottant « Retour en haut » */}
      <ScrollToTop threshold={500} />
    </div>
  );
};

export default ReclamationsNotes;
